import { Dispatch } from 'redux';
import fetchApi from '../services/api';
import * as types from './actionTypes';
import track from '../lib/track';
import { forceLoginHelper } from './auth';
import { setFirstShowingScheduled } from './dashboard';
import EventEmitter from '../lib/eventEmitter';

const endPoints = {
  showing: (id: number) => `/showings/${id}?openHouses=true`,
  showingRequest: '/listings/showing-request?openHouses=true',
  showings: '/showings?openHouses=true',
};

export function emitShowingNotification(
  responseStatus: 'success' | 'error' | 'alert',
  responseMessage: string,
) {
  return EventEmitter.emit('showingNotification', {
    responseMessage,
    responseStatus,
  });
}

export function showingSubmitting(submitLoading: boolean = false) {
  return {
    submitLoading,
    type: types.ShowingSubmitLoading,
  };
}

export function showingDeleteLoader(showingDeleteLoading: boolean = false) {
  return {
    showingDeleteLoading,
    type: types.ShowingDeleteLoading,
  };
}

export function toggleShowingsLoader(showingsLoading: boolean = false) {
  return {
    showingsLoading,
    type: types.ShowingsFetchLoading,
  };
}

export function deleteShowingSuccess(deletedShowingId: number) {
  return {
    deletedShowingId,
    type: types.DeleteShowingSuccess,
  };
}

export function deleteShowingFailure() {
  return {
    type: types.DeleteShowingFailure,
  };
}

export function fetchShowings(token: string, refresh: boolean = true) {
  return async (dispatch: Dispatch<any>) => {
    try {
      if (refresh) {
        dispatch(toggleShowingsLoader(true));
      }
      const response = await fetchApi(endPoints.showings, {}, 'get', {}, token);
      const json = await response.json();
      if (response.ok) {
        if (json.results.length > 0) {
          dispatch(setFirstShowingScheduled());
        }
        dispatch({
          showings: json.results,
          type: types.ShowingsFetchSuccess,
        });
      } else {
        dispatch(
          forceLoginHelper(json, () => {
            dispatch({
              showings: [],
              type: types.ShowingsFetchFailure,
            });
          }),
        );
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}

/**
 * Submit a showing request to Torii
 * @param {Object} values - values
 * @param {type} token - A user's authentication token
 * @param {Function} successCallback - callback function which execuates on success
 * @param {boolean} isOpenHouse - if this a open house visit
 */
export function submitShowingRequest(
  values: {},
  token: string,
  successCallback?: Function,
  isOpenHouse?: boolean,
) {
  return async (dispatch: Dispatch<any>) => {
    dispatch({
      type: types.ClearMessages,
    });
    dispatch(showingSubmitting(true));
    try {
      const response = await fetchApi(
        endPoints.showingRequest,
        values,
        'post',
        {},
        token,
      );
      const json = await response.json();
      if (response.ok) {
        dispatch(setFirstShowingScheduled());
        track.scheduleShowing(values);
        const { showings, msg } = json;
        dispatch({
          showings,
          type: types.ShowingRequestSuccess,
        });
        successCallback && successCallback();
        emitShowingNotification(
          'success',
          isOpenHouse
            ? 'Attending open house has been confirmed.'
            : 'Request received. Your Team will reach out once your showing is confirmed.',
        );
      } else {
        dispatch(
          forceLoginHelper(json, () => {
            emitShowingNotification(
              'error',
              'Something went wrong while submitting your request.',
            );
            dispatch({
              messages: [json],
              type: types.ShowingRequestFailure,
            });
          }),
        );
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}

export function updateShowing(
  showingId: number,
  values: any,
  token: string,
  successCallback?: Function,
) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(showingSubmitting(true));
    try {
      const response = await fetchApi(
        endPoints.showing(showingId),
        values,
        'put',
        {},
        token,
      );
      const json = await response.json();
      if (response.ok) {
        dispatch({
          showings: json.results,
          type: types.ShowingsUpdateSuccess,
        });
        successCallback && successCallback();
        emitShowingNotification('success', 'Your tour time has been updated.');
      } else {
        emitShowingNotification('error', 'Oops! something went wrong.');
        dispatch({
          type: types.ShowingsUpdateFailure,
        });
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}

export function deleteShowing(
  showindId: number,
  token: string,
  successCallback?: Function,
  isOpenHouse?: boolean,
) {
  return async (dispatch: any) => {
    dispatch(showingDeleteLoader(true));
    try {
      const response = await fetchApi(
        endPoints.showing(showindId),
        {},
        'delete',
        {},
        token,
      );
      const json = await response.json();
      if (response.ok) {
        const { deletedShowing } = json;
        dispatch(deleteShowingSuccess(deletedShowing));
        successCallback && successCallback();
        emitShowingNotification(
          'error',
          isOpenHouse
            ? 'Open house visit has been deleted'
            : 'You’ve deleted a scheduled tour.',
        );
        // Once we remove a showing, we want to make sure we have the most current persisted data
        dispatch(fetchShowings(token, false));
      } else {
        emitShowingNotification('alert', json.msg);
        dispatch(deleteShowingFailure());
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}
